<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { vOnClickOutside } from '@vueuse/components';
import { useMapGetter, useStore } from 'dashboard/composables/store';

import BulkSelectBar from 'dashboard/components-next/captain/assistant/BulkSelectBar.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import LabelActions from 'dashboard/components/widgets/conversation/conversationBulkActions/LabelActions.vue';
import Policy from 'dashboard/components/policy.vue';
import { CONTACT_PORTFOLIO_BULK_PERMISSIONS } from 'dashboard/constants/permissions';

const props = defineProps({
  visibleContactIds: {
    type: Array,
    default: () => [],
  },
  selectedContactIds: {
    type: Array,
    default: () => [],
  },
  totalItems: {
    type: Number,
    default: 0,
  },
  selectAllResults: {
    type: Boolean,
    default: false,
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits([
  'clearSelection',
  'toggleAll',
  'selectAllResults',
  'assignResponsible',
  'assignDepartment',
  'addLabels',
  'removeLabels',
  'removeFromPortfolio',
]);

const { t } = useI18n();
const store = useStore();
const agents = useMapGetter('agents/getVerifiedAgents');
const teams = useMapGetter('teams/getTeams');
const teamMembers = useMapGetter('teams/getTeamMembers');

const showAgentSelector = ref(false);
const showDepartmentSelector = ref(false);
const showAddLabelSelector = ref(false);
const showRemoveLabelSelector = ref(false);
const selectedAgentId = ref('');
const selectedTeamId = ref('');
const selectedDepartmentAgentId = ref('');

const selectedCount = computed(() => props.selectedContactIds.length);
const totalVisibleContacts = computed(() => props.visibleContactIds.length);
const effectiveCount = computed(() =>
  props.selectAllResults ? props.totalItems : selectedCount.value
);

const allPageSelected = computed(
  () =>
    totalVisibleContacts.value > 0 &&
    selectedCount.value === totalVisibleContacts.value
);

const showSelectAllResultsBanner = computed(
  () =>
    allPageSelected.value &&
    !props.selectAllResults &&
    props.totalItems > totalVisibleContacts.value
);

const selectAllLabel = computed(() => {
  if (!totalVisibleContacts.value) return '';
  return t('CONTACT_PORTFOLIO.BULK_ACTIONS.SELECT_PAGE', {
    count: totalVisibleContacts.value,
  });
});

const selectedCountLabel = computed(() =>
  props.selectAllResults
    ? t('CONTACT_PORTFOLIO.BULK_ACTIONS.ALL_RESULTS_SELECTED', {
        count: props.totalItems,
      })
    : t('CONTACT_PORTFOLIO.BULK_ACTIONS.SELECTED_COUNT', {
        count: selectedCount.value,
      })
);

const allItems = computed(() =>
  props.visibleContactIds.map(id => ({ id }))
);

const selectionModel = computed({
  get: () => new Set(props.selectedContactIds),
  set: newSet => {
    if (!props.visibleContactIds.length) {
      emit('toggleAll', false);
      return;
    }
    const shouldSelectAll =
      newSet.size === props.visibleContactIds.length && newSet.size > 0;
    emit('toggleAll', shouldSelectAll);
  },
});

const agentOptions = computed(() =>
  (agents.value || []).map(agent => ({
    value: String(agent.id),
    label: agent.name,
  }))
);

const teamOptions = computed(() =>
  (teams.value || []).map(team => ({
    value: String(team.id),
    label: team.name,
  }))
);

const departmentAgentOptions = computed(() => {
  if (!selectedTeamId.value) return [];
  const members = teamMembers.value?.[selectedTeamId.value] || [];
  return members.map(member => ({
    value: String(member.id),
    label: member.name,
  }));
});

const closeAllSelectors = () => {
  showAgentSelector.value = false;
  showDepartmentSelector.value = false;
  showAddLabelSelector.value = false;
  showRemoveLabelSelector.value = false;
};

const emitClearSelection = () => {
  closeAllSelectors();
  emit('clearSelection');
};

const toggleAgentSelector = () => {
  if (!effectiveCount.value || props.isLoading) return;
  closeAllSelectors();
  showAgentSelector.value = !showAgentSelector.value;
};

const toggleDepartmentSelector = () => {
  if (!effectiveCount.value || props.isLoading) return;
  closeAllSelectors();
  showDepartmentSelector.value = !showDepartmentSelector.value;
};

const toggleAddLabelSelector = () => {
  if (!effectiveCount.value || props.isLoading) return;
  closeAllSelectors();
  showAddLabelSelector.value = !showAddLabelSelector.value;
};

const toggleRemoveLabelSelector = () => {
  if (!effectiveCount.value || props.isLoading) return;
  closeAllSelectors();
  showRemoveLabelSelector.value = !showRemoveLabelSelector.value;
};

const handleAssignResponsible = () => {
  const agent = (agents.value || []).find(
    item => String(item.id) === selectedAgentId.value
  );
  if (!agent) return;

  emit('assignResponsible', { agentId: agent.id, agentName: agent.name });
  selectedAgentId.value = '';
  closeAllSelectors();
};

const handleAssignDepartment = () => {
  const team = (teams.value || []).find(
    item => String(item.id) === selectedTeamId.value
  );
  const agent = departmentAgentOptions.value.find(
    item => item.value === selectedDepartmentAgentId.value
  );
  if (!team || !agent) return;

  emit('assignDepartment', {
    teamId: Number(team.id),
    teamName: team.name,
    agentId: Number(agent.value),
    agentName: agent.label,
  });
  selectedTeamId.value = '';
  selectedDepartmentAgentId.value = '';
  closeAllSelectors();
};

const handleAddLabels = labels => {
  emit('addLabels', labels);
  closeAllSelectors();
};

const handleRemoveLabels = labels => {
  emit('removeLabels', labels);
  closeAllSelectors();
};

const handleRemoveFromPortfolio = () => {
  emit('removeFromPortfolio');
  closeAllSelectors();
};

watch(selectedTeamId, teamId => {
  selectedDepartmentAgentId.value = '';
  if (teamId) {
    store.dispatch('teamMembers/get', { teamId });
  }
});
</script>

<template>
  <Policy
    :permissions="[
      'administrator',
      'agent',
      CONTACT_PORTFOLIO_BULK_PERMISSIONS,
    ]"
  >
    <div class="flex flex-col gap-2">
      <div
        v-if="showSelectAllResultsBanner"
        class="rounded-lg border border-n-amber-6 bg-n-amber-3 px-4 py-2 text-sm text-n-amber-12"
      >
        <span>
          {{
            t('CONTACT_PORTFOLIO.BULK_ACTIONS.SELECT_ALL_RESULTS_PROMPT', {
              count: totalItems,
            })
          }}
        </span>
        <Button
          sm
          link
          class="ml-2"
          :label="
            t('CONTACT_PORTFOLIO.BULK_ACTIONS.SELECT_ALL_RESULTS', {
              count: totalItems,
            })
          "
          @click="emit('selectAllResults', true)"
        />
      </div>

      <BulkSelectBar
        v-model="selectionModel"
        :all-items="allItems"
        :select-all-label="selectAllLabel"
        :selected-count-label="selectedCountLabel"
        class="py-2 ltr:!pr-3 rtl:!pl-3 justify-between"
      >
        <template #secondary-actions>
          <Button
            sm
            ghost
            slate
            :label="t('CONTACT_PORTFOLIO.BULK_ACTIONS.CLEAR_SELECTION')"
            class="!px-1.5"
            @click="emitClearSelection"
          />
        </template>
        <template #actions>
          <div class="flex flex-wrap items-center gap-2 ml-auto">
            <div
              v-on-click-outside="() => (showAgentSelector = false)"
              class="relative flex items-center"
            >
              <Button
                sm
                faded
                slate
                icon="i-lucide-user-round-plus"
                :label="t('CONTACT_PORTFOLIO.BULK_ACTIONS.ASSIGN_RESPONSIBLE')"
                :disabled="!effectiveCount || isLoading"
                :is-loading="isLoading"
                class="[&>span:nth-child(2)]:hidden sm:[&>span:nth-child(2)]:inline w-fit"
                @click="toggleAgentSelector"
              />
              <div
                v-if="showAgentSelector"
                class="absolute top-9 z-20 w-72 rounded-lg border border-n-weak bg-n-solid-1 p-3 shadow-md ltr:right-0 rtl:left-0"
              >
                <p class="mb-2 text-xs font-medium text-n-slate-11">
                  {{ t('CONTACT_PORTFOLIO.BULK_ACTIONS.SELECT_RESPONSIBLE') }}
                </p>
                <ComboBox
                  v-model="selectedAgentId"
                  :options="agentOptions"
                  :placeholder="
                    t('CONTACT_PORTFOLIO.FILTERS.RESPONSIBLE_AGENT_PLACEHOLDER')
                  "
                />
                <Button
                  sm
                  class="mt-3 w-full"
                  :label="t('CONTACT_PORTFOLIO.BULK_ACTIONS.CONTINUE')"
                  :disabled="!selectedAgentId"
                  @click="handleAssignResponsible"
                />
              </div>
            </div>

            <div
              v-on-click-outside="() => (showDepartmentSelector = false)"
              class="relative flex items-center"
            >
              <Button
                sm
                faded
                slate
                icon="i-lucide-users-round"
                :label="t('CONTACT_PORTFOLIO.BULK_ACTIONS.ASSIGN_DEPARTMENT')"
                :disabled="!effectiveCount || isLoading"
                :is-loading="isLoading"
                class="[&>span:nth-child(2)]:hidden sm:[&>span:nth-child(2)]:inline w-fit"
                @click="toggleDepartmentSelector"
              />
              <div
                v-if="showDepartmentSelector"
                class="absolute top-9 z-20 w-72 rounded-lg border border-n-weak bg-n-solid-1 p-3 shadow-md ltr:right-0 rtl:left-0"
              >
                <p class="mb-2 text-xs font-medium text-n-slate-11">
                  {{ t('CONTACT_PORTFOLIO.BULK_ACTIONS.SELECT_DEPARTMENT') }}
                </p>
                <ComboBox
                  v-model="selectedTeamId"
                  :options="teamOptions"
                  :placeholder="
                    t('CONTACT_PORTFOLIO.FILTERS.DEPARTMENT_PLACEHOLDER')
                  "
                  class="mb-2"
                />
                <ComboBox
                  v-model="selectedDepartmentAgentId"
                  :options="departmentAgentOptions"
                  :placeholder="
                    t('CONTACT_PORTFOLIO.BULK_ACTIONS.SELECT_DEPARTMENT_AGENT')
                  "
                  :disabled="!selectedTeamId"
                />
                <Button
                  sm
                  class="mt-3 w-full"
                  :label="t('CONTACT_PORTFOLIO.BULK_ACTIONS.CONTINUE')"
                  :disabled="!selectedTeamId || !selectedDepartmentAgentId"
                  @click="handleAssignDepartment"
                />
              </div>
            </div>

            <div
              v-on-click-outside="() => (showAddLabelSelector = false)"
              class="relative flex items-center"
            >
              <Button
                sm
                faded
                slate
                icon="i-lucide-tags"
                :label="t('CONTACT_PORTFOLIO.BULK_ACTIONS.ADD_TAGS')"
                :disabled="!effectiveCount || isLoading"
                :is-loading="isLoading"
                class="[&>span:nth-child(2)]:hidden sm:[&>span:nth-child(2)]:inline w-fit"
                @click="toggleAddLabelSelector"
              />
              <transition
                enter-active-class="transition ease-out duration-100"
                enter-from-class="transform opacity-0 scale-95"
                enter-to-class="transform opacity-100 scale-100"
                leave-active-class="transition ease-in duration-75"
                leave-from-class="transform opacity-100 scale-100"
                leave-to-class="transform opacity-0 scale-95"
              >
                <LabelActions
                  v-if="showAddLabelSelector"
                  class="[&>.triangle]:!hidden [&>div>button]:!hidden ltr:!right-0 rtl:!left-0 top-8 mt-0.5"
                  @assign="handleAddLabels"
                />
              </transition>
            </div>

            <div
              v-on-click-outside="() => (showRemoveLabelSelector = false)"
              class="relative flex items-center"
            >
              <Button
                sm
                faded
                slate
                icon="i-lucide-tag"
                :label="t('CONTACT_PORTFOLIO.BULK_ACTIONS.REMOVE_TAGS')"
                :disabled="!effectiveCount || isLoading"
                :is-loading="isLoading"
                class="[&>span:nth-child(2)]:hidden sm:[&>span:nth-child(2)]:inline w-fit"
                @click="toggleRemoveLabelSelector"
              />
              <transition
                enter-active-class="transition ease-out duration-100"
                enter-from-class="transform opacity-0 scale-95"
                enter-to-class="transform opacity-100 scale-100"
                leave-active-class="transition ease-in duration-75"
                leave-from-class="transform opacity-100 scale-100"
                leave-to-class="transform opacity-0 scale-95"
              >
                <LabelActions
                  v-if="showRemoveLabelSelector"
                  class="[&>.triangle]:!hidden [&>div>button]:!hidden ltr:!right-0 rtl:!left-0 top-8 mt-0.5"
                  @assign="handleRemoveLabels"
                />
              </transition>
            </div>

            <Button
              sm
              faded
              ruby
              icon="i-lucide-user-minus"
              :label="t('CONTACT_PORTFOLIO.BULK_ACTIONS.REMOVE_FROM_PORTFOLIO')"
              :disabled="!effectiveCount || isLoading"
              :is-loading="isLoading"
              class="[&>span:nth-child(2)]:hidden sm:[&>span:nth-child(2)]:inline w-fit"
              @click="handleRemoveFromPortfolio"
            />
          </div>
        </template>
      </BulkSelectBar>
    </div>
  </Policy>
</template>
